import { apiClient } from './client';

/** One row of the conversations table. */
export interface ConversationRow {
  id: string;
  entityType: string | null;
  entityId: string | null;
  /** The record's name when it has one. Null is "no name to be had" — never a fallback. */
  entityName: string | null;
  subject: string | null;
  status: string;
  kind: string | null;
  updatedAt: string;
  messageCount?: number;
  unreadCount?: number;
  preview?: string | null;
  previewSender?: string | null;
  participants: { recipientId: string; name: string | null }[];
}

export interface ConversationPage {
  items: ConversationRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface ConversationQuery {
  entityType?: string;
  statuses?: string[];
  withParticipant?: string;
  search?: string;
  /**
   * The range bounds **last activity**, not creation — a thread opened in March
   * and answered yesterday belongs in yesterday's range, and that is also the
   * order the list comes back in.
   */
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

/**
 * Every conversation the signed-in person is on (COMMUNICATIONS-DESIGN H2).
 *
 * ⚠️ **There is no `participantId` here on purpose.** The server always scopes
 * the list to the caller and never reads that from the query; `withParticipant`
 * narrows *within* it to threads a second person is also on, which can only
 * ever return a subset of what the caller could already read.
 */
export async function fetchConversations(query: ConversationQuery): Promise<ConversationPage> {
  const { data } = await apiClient.get('/threads/conversations', {
    params: {
      ...query,
      // Repeatable params are sent comma-joined; the server accepts either.
      ...(query.statuses && query.statuses.length > 0
        ? { statuses: query.statuses.join(',') }
        : { statuses: undefined }),
    },
  });
  return data as ConversationPage;
}
